import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import './Auth.css';

function SetPassword() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { language, toggleLanguage } = useLanguage();
    
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [verifying, setVerifying] = useState(true);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);
    const [tokenValid, setTokenValid] = useState(true);
    const [clientInfo, setClientInfo] = useState(null);

    const token = searchParams.get('token');

    useEffect(() => {
        if (!token) {
            setTokenValid(false);
            setVerifying(false);
            setError(language === 'ar' 
                ? 'رابط التفعيل غير صالح أو مفقود.'
                : 'Invalid or missing activation link.');
            return;
        }

        // Verify token and get client info
        verifyToken();
    }, [token]);

    const verifyToken = async () => {
        try {
            const response = await fetch(`/api/v1/helpdesk/client/verify-setup-token?token=${token}`);
            const data = await response.json();

            if (data.success) {
                setClientInfo(data.client);
                setTokenValid(true);
            } else {
                setTokenValid(false);
                setError(data.message || (language === 'ar' 
                    ? 'رابط التفعيل غير صالح أو منتهي الصلاحية.'
                    : 'This activation link is invalid or has expired.'));
            }
        } catch (err) {
            setTokenValid(false);
            setError(language === 'ar' 
                ? 'خطأ في الاتصال. حاول مرة أخرى.'
                : 'Connection error. Please try again.');
        } finally {
            setVerifying(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        // Validate passwords
        if (newPassword.length < 6) {
            setError(language === 'ar' 
                ? 'يجب أن تكون كلمة المرور 6 أحرف على الأقل'
                : 'Password must be at least 6 characters long');
            return;
        }

        if (newPassword !== confirmPassword) {
            setError(language === 'ar' 
                ? 'كلمات المرور غير متطابقة'
                : 'Passwords do not match');
            return;
        }

        setLoading(true);

        try {
            const response = await fetch('/api/v1/helpdesk/client/set-password', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ 
                    token: token,
                    password: newPassword 
                })
            });

            const data = await response.json();

            if (data.success) {
                setSuccess(true);
            } else {
                setError(data.message || (language === 'ar' 
                    ? 'فشل في تعيين كلمة المرور. حاول مرة أخرى.'
                    : 'Failed to set password. Please try again.'));
                if (data.message && data.message.includes('expired')) {
                    setTokenValid(false);
                }
            }
        } catch (err) {
            setError(language === 'ar' 
                ? 'خطأ في الاتصال. حاول مرة أخرى.'
                : 'Connection error. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    // Loading State
    if (verifying) {
        return (
            <div className={`auth-page ${language === 'ar' ? 'rtl' : 'ltr'}`}>
                <div className="auth-container">
                    <div className="auth-logo">
                        <img src="/logo.png" alt="Matix ERP" />
                    </div>
                    <div className="loading-spinner"></div>
                    <p>{language === 'ar' ? 'جاري التحقق...' : 'Verifying...'}</p>
                </div>
            </div>
        );
    }

    // Success State
    if (success) {
        return (
            <div className={`auth-page ${language === 'ar' ? 'rtl' : 'ltr'}`}>
                <div className="auth-container">
                    <button onClick={toggleLanguage} className="lang-toggle">
                        🌐 {language === 'ar' ? 'English' : 'العربية'}
                    </button>
                    
                    <div className="auth-logo">
                        <img src="/logo.png" alt="Matix ERP" />
                    </div>
                    
                    <div className="success-icon-wrapper">
                        <span className="success-icon-large">✓</span>
                    </div>
                    
                    <h1>{language === 'ar' ? 'تم بنجاح!' : 'Welcome!'}</h1>
                    <p className="auth-subtitle">
                        {language === 'ar' 
                            ? 'تم تفعيل حسابك وتعيين كلمة المرور بنجاح.'
                            : 'Your account has been activated and password set successfully.'}
                    </p>

                    <div className="success-message-box">
                        <p>
                            {language === 'ar' 
                                ? 'يمكنك الآن تسجيل الدخول ومتابعة تذاكر الدعم الخاصة بك.'
                                : 'You can now log in and track your support tickets.'}
                        </p>
                    </div>

                    <button 
                        className="auth-submit"
                        onClick={() => navigate('/login')}
                    >
                        {language === 'ar' ? '🔐 تسجيل الدخول الآن' : '🔐 Login Now'}
                    </button>

                    <p className="auth-footer">© 2024 MatixERP. All rights reserved.</p>
                </div>
            </div>
        );
    }

    // Invalid Token State
    if (!tokenValid) {
        return (
            <div className={`auth-page ${language === 'ar' ? 'rtl' : 'ltr'}`}>
                <div className="auth-container">
                    <button onClick={toggleLanguage} className="lang-toggle">
                        🌐 {language === 'ar' ? 'English' : 'العربية'}
                    </button>
                    
                    <div className="auth-logo">
                        <img src="/logo.png" alt="Matix ERP" />
                    </div>
                    
                    <div className="error-icon-wrapper">
                        <span className="error-icon-large">✕</span>
                    </div>
                    
                    <h1>{language === 'ar' ? 'رابط غير صالح' : 'Invalid Link'}</h1>
                    <p className="auth-subtitle">
                        {language === 'ar' 
                            ? 'رابط تفعيل الحساب غير صالح أو منتهي الصلاحية.'
                            : 'This account activation link is invalid or has expired.'}
                    </p>

                    <div className="error-message-box">
                        <p>{error}</p>
                    </div>

                    <p className="auth-subtitle" style={{ marginTop: '20px' }}>
                        {language === 'ar' 
                            ? 'إذا كان لديك حساب بالفعل:'
                            : 'If you already have an account:'}
                    </p>

                    <Link to="/login" className="auth-submit" style={{ textDecoration: 'none', textAlign: 'center', display: 'block' }}>
                        {language === 'ar' ? '← تسجيل الدخول' : '← Login'}
                    </Link>

                    <p className="auth-footer">© 2024 MatixERP. All rights reserved.</p>
                </div>
            </div>
        );
    }

    // Set Password Form
    return (
        <div className={`auth-page ${language === 'ar' ? 'rtl' : 'ltr'}`}>
            <div className="auth-container">
                <button onClick={toggleLanguage} className="lang-toggle">
                    🌐 {language === 'ar' ? 'English' : 'العربية'}
                </button>
                
                <div className="auth-logo">
                    <img src="/logo.png" alt="Matix ERP" />
                </div>
                
                <h1>{language === 'ar' ? 'مرحباً بك! 👋' : 'Welcome! 👋'}</h1>
                <p className="auth-subtitle">
                    {language === 'ar' 
                        ? 'يرجى تعيين كلمة مرور للوصول إلى حسابك'
                        : 'Please set a password to access your account'}
                </p>

                {clientInfo && (
                    <div className="client-info-box">
                        <p><strong>{language === 'ar' ? 'الاسم:' : 'Name:'}</strong> {clientInfo.name}</p>
                        <p><strong>{language === 'ar' ? 'البريد الإلكتروني:' : 'Email:'}</strong> {clientInfo.email}</p>
                    </div>
                )}

                {error && <div className="auth-error">{error}</div>}

                <form onSubmit={handleSubmit} className="auth-form">
                    <div className="form-group">
                        <label>{language === 'ar' ? 'كلمة المرور' : 'Password'}</label>
                        <div className="password-input">
                            <input
                                type={showPassword ? 'text' : 'password'}
                                value={newPassword}
                                onChange={(e) => setNewPassword(e.target.value)}
                                placeholder={language === 'ar' ? 'أدخل كلمة المرور (6 أحرف على الأقل)' : 'Enter password (min 6 characters)'}
                                required
                                minLength={6}
                                disabled={loading}
                            />
                            <button
                                type="button"
                                className="password-toggle"
                                onClick={() => setShowPassword(!showPassword)}
                            >
                                {showPassword ? (language === 'ar' ? 'إخفاء' : 'Hide') : (language === 'ar' ? 'إظهار' : 'Show')}
                            </button>
                        </div>
                    </div>

                    <div className="form-group">
                        <label>{language === 'ar' ? 'تأكيد كلمة المرور' : 'Confirm Password'}</label>
                        <div className="password-input">
                            <input
                                type={showConfirmPassword ? 'text' : 'password'}
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                placeholder={language === 'ar' ? 'أعد إدخال كلمة المرور' : 'Re-enter your password'}
                                required
                                minLength={6}
                                disabled={loading}
                            />
                            <button
                                type="button"
                                className="password-toggle"
                                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                            >
                                {showConfirmPassword ? (language === 'ar' ? 'إخفاء' : 'Hide') : (language === 'ar' ? 'إظهار' : 'Show')}
                            </button>
                        </div>
                    </div>

                    {/* Password Requirements */}
                    <div className="password-requirements">
                        <p className={newPassword.length >= 6 ? 'requirement-met' : ''}>
                            {newPassword.length >= 6 ? '✓' : '○'} {language === 'ar' ? '6 أحرف على الأقل' : 'At least 6 characters'}
                        </p>
                        <p className={newPassword && newPassword === confirmPassword ? 'requirement-met' : ''}>
                            {newPassword && newPassword === confirmPassword ? '✓' : '○'} {language === 'ar' ? 'كلمات المرور متطابقة' : 'Passwords match'}
                        </p>
                    </div>

                    <button 
                        type="submit" 
                        className="auth-submit"
                        disabled={loading || newPassword.length < 6 || newPassword !== confirmPassword}
                    >
                        {loading 
                            ? (language === 'ar' ? 'جاري التفعيل...' : 'Activating...') 
                            : (language === 'ar' ? '🚀 تفعيل الحساب' : '🚀 Activate Account')}
                    </button>
                </form>

                <p className="auth-footer">© 2024 MatixERP. All rights reserved.</p>
            </div>
        </div>
    );
}

export default SetPassword;
